import React from "react"
import PropTypes from "prop-types"
import { Field, reduxForm } from "redux-form"
import { defineMessages, injectIntl } from "react-intl"
// Material Ui
import Card from "@material-ui/core/Card"
import CardContent from "@material-ui/core/CardContent"
import CardHeader from "@material-ui/core/CardHeader"
import { Box, FormGroup } from "@material-ui/core"
// ## //
import { Alert, AlertTitle } from "@material-ui/lab"
import { useDispatch, useSelector } from "react-redux"
import {
  ButtonCancel,
  ButtonClear,
  ButtonSubmit
} from "../../component/buttons"
import PasswordRule from "./PasswordRule"
import { words } from "../../i18n/lstrings"
import userIntl from "./userIntl"
import useSmartFormat from "../../i18n/useSmartFormat"
import useLoginStyles from "../login/loginStyle"
import authSlice from "../../api/authSlice"
import FieldPassword from "../../component/forms/FieldPassword"

const { actions: auth } = authSlice

const labels = defineMessages({
  title: {
    id: "chgpassword.form.title",
    defaultMessage: "Change your password"
  },
  firstRegistrationTitle: {
    id: "registration.form.title",
    defaultMessage: "Create your account"
  },
  registrationFailedTitle: {
    id: "registration.form.error.title",
    defaultMessage: "Unable to create your account"
  },
  registrationInformation: {
    id: "registration.form.information",
    defaultMessage:
      "For your first login please enter the password we gave you in your invitation email."
  },
  changePasswordInformation: {
    id: "chgpassword.form.olspassword.information",
    defaultMessage: "Enter your current password."
  },
  oldpassword: {
    id: "chgpassword.form.oldpassword",
    description: "Old password",
    defaultMessage: "Old password"
  },
  temporaryPassword: {
    id: "chgpassword.form.temporaryPassword",
    description: "Temporary password",
    defaultMessage: "Temporary password"
  },
  password: {
    id: "chgpassword.form.password",
    description: "New password",
    defaultMessage: "New password"
  },
  confirmpassword: {
    id: "chgpassword.form.confirmpassword",
    description: "new password confirmation",
    defaultMessage: "Confirm new password"
  }
})

function getViewModel(isFirstRegistration) {
  if (isFirstRegistration) {
    return {
      pageTitle: labels.firstRegistrationTitle,
      alertTitle: labels.registrationFailedTitle,
      information: labels.registrationInformation
    }
  }

  return {
    pageTitle: labels.title,
    alertTitle: labels.registrationFailedTitle,
    information: labels.changePasswordInformation
  }
}

export function ChangePasswordForm({
  handleSubmit,
  pristine,
  reset,
  submitting,
  handleSave,
  handleCancel,
  mustProvideOldPassword,
  register
}) {
  const dispatch = useDispatch()
  const errorInfo = useSelector((state) => state.auth.error)
  const format = useSmartFormat()
  const classes = useLoginStyles()

  const viewLabelModel = getViewModel(register)

  return (
    <Card className={classes.changePwdForm}>
      <CardHeader
        className="clearfix"
        title={format(viewLabelModel.pageTitle)}
      />
      <CardContent>
        {errorInfo && (
          <Alert severity="error" onClose={() => dispatch(auth.clearError())}>
            <AlertTitle>{format(viewLabelModel.alertTitle)}</AlertTitle>
            {format(errorInfo)}
          </Alert>
        )}
        <form onSubmit={handleSubmit(handleSave)}>
          <FormGroup>
            {mustProvideOldPassword && (
              <Field
                component={FieldPassword}
                label={
                    register
                        ? format(labels.temporaryPassword)
                        : format(labels.oldpassword)
                }
                name="oldPassword"
                type="password"
                autoComplete="current-password"
              />
            )}
            <Field
              component={FieldPassword}
              label={format(labels.password)}
              name="password"
              type="password"
            />

            <Field
              component={FieldPassword}
              label={format(labels.confirmpassword)}
              name="confirmPassword"
              type="password"
            />
          </FormGroup>
          <Box className={classes.loginFormButton}>
            <ButtonClear disabled={pristine || submitting} onClick={reset} />
            {handleCancel && <ButtonCancel onClick={handleCancel} />}
            <ButtonSubmit disabled={submitting} />
          </Box>
        </form>
      </CardContent>
    </Card>
  )
}

export const validate = (values, props) => {
  const { formatMessage: format } = props.intl
  const errors = {}

  if (!values.oldPassword) {
    errors.oldPassword = format(words.required)
  }
  if (!values.password) {
    errors.password = format(words.required)
  }
  if (!PasswordRule(values.password)) {
    errors.password = format(userIntl.passwordRule)
  }
  if (!values.confirmPassword) {
    errors.confirmPassword = format(words.required)
  }
  if (values.password !== values.confirmPassword) {
    errors.confirmPassword = format(userIntl.passwordMismatch)
  }

  return errors
}

ChangePasswordForm.defaultProps = {
  mustProvideOldPassword: true,
  register: false,
  handleCancel: undefined
}

ChangePasswordForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  reset: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  handleSave: PropTypes.func.isRequired,
  handleCancel: PropTypes.func,
  intl: PropTypes.any.isRequired,
  mustProvideOldPassword: PropTypes.bool,
  register: PropTypes.bool
}

export default injectIntl(
  reduxForm({
    form: "ChangePasswordForm",
    validate
  })(ChangePasswordForm)
)
